/**
 * KONSERTENE HOS ARRANGØREN — `/v1/concerts` og `/v1/concert-templates` (agent D,
 * `docs/SPILLESTED-KONSERT.md` skjerm 5, 6, 8, 11 og 12).
 *
 * IKKE `concerts.ts`. Den fila er artistens «Konserter» (`/v1/me/concerts`, sak 216): konsertene
 * personen står på, på tvers av hus. Denne er husets egen liste — konserten som arrangøren eier,
 * med artistene som programposter (`performances.concert_id`).
 *
 * BEHOLDEREN VISES ALDRI. Konserten ligger i en årgang av typen `concerts`; `editionId` står i
 * DTO-en fordi panelene som gjenbrukes fra programposten trenger den, ikke for å vises.
 *
 * INGEN PENGER I KONSERTEN. Billettpris, avtale og budsjett er E sine (`concert_budget:*`), og
 * lista får avtale og break-even derfra bare når leseren har `concert_budget:read`.
 */
import { z } from 'zod';
import { engagementRefDtoSchema } from './engagements.js';

import { CONCERT_DEAL_TYPES } from '../concerts/deal-math.js';
import { bookingBidStatusSchema, performanceStatusSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { MAX_COST_AMOUNT_MINOR } from './economy.js';

const dateOnly = z.iso.date();
const instant = z.iso.datetime({ offset: true });
/** Et klokkeslett i konsertens sone, `HH:MM`. */
export const concertClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

const shortText = z.string().trim().min(1).max(160);
const optionalText = z.string().trim().max(160).nullish();

/* ------------------------------------------------------------------------------ statusene */

/**
 * Forespurt → 1./2. hold → Bekreftet → Annonsert → I salg → Gjennomført → Oppgjort.
 * `draft` er en konsert uten forespørsel ennå; `declined` og `cancelled` står utenfor kjeden.
 */
export const CONCERT_STATUSES = [
  'draft',
  'requested',
  'hold',
  'confirmed',
  'announced',
  'on_sale',
  'performed',
  'settled',
  'declined',
  'cancelled',
] as const;
export const concertStatusSchema = z.enum(CONCERT_STATUSES);
export type ConcertStatus = z.infer<typeof concertStatusSchema>;

/** Kjeden som vises øverst på konsertsiden, i rekkefølge. */
export const CONCERT_STATUS_CHAIN = [
  'requested',
  'hold',
  'confirmed',
  'announced',
  'on_sale',
  'performed',
  'settled',
] as const satisfies readonly ConcertStatus[];

/**
 * Statusene arrangøren setter for hånd. «Oppgjort» setter oppgjøret (E). Forespurt, hold og
 * bekreftet settes for hånd bare når stedet ikke er i Tvitvi — ellers er det huset som svarer (C).
 */
export const MANUAL_CONCERT_STATUSES = [
  'draft',
  'requested',
  'hold',
  'confirmed',
  'announced',
  'on_sale',
  'performed',
  'cancelled',
] as const satisfies readonly ConcertStatus[];

export const CONCERT_BILLINGS = ['headliner', 'support'] as const;
export const concertBillingSchema = z.enum(CONCERT_BILLINGS);
export type ConcertBilling = z.infer<typeof concertBillingSchema>;

export const CONCERT_ADVANCE_STATES = ['not_started', 'in_progress', 'ready'] as const;
export const concertAdvanceStateSchema = z.enum(CONCERT_ADVANCE_STATES);
export type ConcertAdvanceState = z.infer<typeof concertAdvanceStateSchema>;

/** Hvor stedet kommer fra: en sal i Tvitvi, stedsregisteret, eller bare et navn. */
export const CONCERT_VENUE_SOURCES = ['tvitvi', 'registry', 'text', 'none'] as const;
export const concertVenueSourceSchema = z.enum(CONCERT_VENUE_SOURCES);
export type ConcertVenueSource = z.infer<typeof concertVenueSourceSchema>;

/* ------------------------------------------------------------------------------ konserten */

export const concertPerformanceSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    billing: concertBillingSchema.nullable(),
    bookingArtistId: z.uuid().nullable(),
    formatName: z.string().nullable(),
    advanceShared: z.boolean(),
    /** Siste bud til byrået på denne artisten (QA G-9). Null: ingen bud sendt. */
    bidStatus: bookingBidStatusSchema.nullable().optional(),
    /** Det samme budet (F22): «Godta motbudet» trenger raden. Beløpet leses fra budet selv. */
    bidId: z.uuid().nullable().optional(),
    /** Byrået i Tvitvi artisten sendes bud til; null når byrået ikke er koblet. */
    agencyName: z.string().nullable().optional(),
  })
  .meta({ id: 'ConcertPerformance' });
export type ConcertPerformance = z.infer<typeof concertPerformanceSchema>;

const personRefSchema = z.object({ personId: z.uuid(), name: z.string() });
export type ConcertPersonRef = z.infer<typeof personRefSchema>;

export const concertDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    title: z.string().nullable(),
    startsOn: dateOnly,
    timezone: z.string(),
    venueSource: concertVenueSourceSchema,
    venueOrganizationId: z.uuid().nullable(),
    venueRoomId: z.uuid().nullable(),
    venueDirectoryRoomId: z.uuid().nullable(),
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    doorsAt: instant.nullable(),
    curfewAt: instant.nullable(),
    getInAt: instant.nullable(),
    layoutName: z.string().nullable(),
    capacity: z.number().int().nullable(),
    /** QA16 funn 13: the room's capacity in Tvitvi (the chosen layout, else standing); null outside. */
    roomCapacity: z.number().int().nullable().default(null),
    status: concertStatusSchema,
    holdRank: z.number().int().nullable(),
    holdUntil: dateOnly.nullable(),
    announcedOn: dateOnly.nullable(),
    onSaleOn: dateOnly.nullable(),
    tag: z.string().nullable(),
    responsible: personRefSchema.nullable(),
    templateId: z.uuid().nullable(),
    productionMode: z.enum(['own', 'sold']),
    expectedAudience: z.number().int().nullable(),
    /** Satt når huset har bekreftet og kvelden finnes i husets kalender. */
    venueNightId: z.uuid().nullable(),
    notes: z.string().nullable(),
    /** Hovedartist først, så support, så resten i rekkefølge. */
    performances: z.array(concertPerformanceSchema),
    documentFolderId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** Engasjementet raden hører til (0927, grep F1): den ene statusen alle flatene leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
  })
  .meta({ id: 'Concert' });
export type ConcertDto = z.infer<typeof concertDtoSchema>;

/* ------------------------------------------------------------------------------ lista */

export const CONCERT_WHEN = ['upcoming', 'past', 'all'] as const;
export const concertWhenSchema = z.enum(CONCERT_WHEN);
export type ConcertWhen = z.infer<typeof concertWhenSchema>;

export const CONCERT_SORT_KEYS = ['date'] as const;

/** `?status=hold,confirmed` — kommaformen, som programpostene. */
const commaStatuses = z
  .string()
  .max(200)
  .transform((value) => value.split(',').filter((part) => part.length > 0))
  .pipe(z.array(concertStatusSchema).max(CONCERT_STATUSES.length));

export const listConcertsQuerySchema = cursorQuerySchema(CONCERT_SORT_KEYS)
  .extend({
    when: concertWhenSchema.default('upcoming'),
    q: z.string().trim().max(120).optional(),
    tag: z.string().trim().max(120).optional(),
    status: commaStatuses.optional(),
    responsiblePersonId: z.uuid().optional(),
    /** Kalendervisningen: én måned, uavhengig av `when`. */
    from: dateOnly.optional(),
    to: dateOnly.optional(),
  })
  .meta({ id: 'ListConcertsQuery' });
export type ListConcertsQuery = z.infer<typeof listConcertsQuerySchema>;

export const concertListItemSchema = z
  .object({
    id: z.uuid(),
    startsOn: dateOnly,
    timezone: z.string(),
    title: z.string().nullable(),
    /** Hovedartisten, eller den første artisten. Null er «Ikke avklart». */
    artistName: z.string().nullable(),
    formatName: z.string().nullable(),
    supportNames: z.array(z.string()),
    venueSource: concertVenueSourceSchema,
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
    city: z.string().nullable(),
    status: concertStatusSchema,
    holdRank: z.number().int().nullable(),
    holdUntil: dateOnly.nullable(),
    announcedOn: dateOnly.nullable(),
    onSaleOn: dateOnly.nullable(),
    capacity: z.number().int().nullable(),
    layoutName: z.string().nullable(),
    tag: z.string().nullable(),
    responsible: personRefSchema.nullable(),
    advance: concertAdvanceStateSchema,
    /**
     * Konsertens programposter, hovedartisten først (ADVANCE-TILGANG §6): kolonnen «Tilgang» slår
     * opp sammendraget for dem og viser summen.
     */
    performanceIds: z.array(z.uuid()).default([]),
    /** Fra budsjettet (E). Null uten `concert_budget:read`, og når ingen avtale finnes. */
    dealLabel: z.string().nullable(),
    dealType: z.string().nullable(),
    /** QA16 funn 12: the terms behind the type, read by the one deal formatter. */
    dealGuaranteeMinor: z.number().int().nullable().default(null),
    dealArtistPercent: z.number().nullable().default(null),
    dealAfterCosts: z.boolean().nullable().default(null),
    dealCurrency: z.string().nullable().default(null),
    breakEvenTickets: z.number().int().nullable(),
    /**
     * Om konserten har en billettpris. Uten den er break-even i billetter meningsløst («0» ville
     * lest som «vi går i null uten å selge noe»), og lista viser «—» med en forklaring (G-13).
     * Null uten `concert_budget:read`, som avtalen.
     */
    hasTicketPrice: z.boolean().nullable(),
    /** Stedsregisteret, eller salen i Tvitvi gjennom `app.concert_room_facts` (0416). */
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    /**
     * Bølge 3 («alle feltene i Kolonner»): det konserten selv sender under den samme `concert:read`,
     * så lista kan vise dem som skjulte kolonner. Tidene er instanter; klienten viser dem i `timezone`.
     */
    address: z.string().nullable(),
    doorsAt: instant.nullable(),
    curfewAt: instant.nullable(),
    getInAt: instant.nullable(),
    expectedAudience: z.number().int().nullable(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'ConcertListItem' });
export type ConcertListItem = z.infer<typeof concertListItemSchema>;

export const concertListResponseSchema = page(concertListItemSchema, 'ConcertListResponse');
export type ConcertListResponse = z.infer<typeof concertListResponseSchema>;

/*
 * DE SOLGTE (sak 233, 0470) — `GET /v1/concerts/sold`. Byråets kvelder hos en arrangør: budet er
 * godtatt og programposten finnes. Samme `when`/`from`/`to` og samme retning som lista over, så
 * «Alle» kan flette de to på dato. INGEN av arrangørens tall: `feeMinor` er byråets eget godtatte
 * bud (speilraden i byråets forespørsler), og null uten `performance:list`.
 */
export const listSoldGigsQuerySchema = cursorQuerySchema(CONCERT_SORT_KEYS)
  .extend({
    when: concertWhenSchema.default('upcoming'),
    q: z.string().trim().max(120).optional(),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
  })
  .meta({ id: 'ListSoldGigsQuery' });
export type ListSoldGigsQuery = z.infer<typeof listSoldGigsQuerySchema>;

export const soldGigSchema = z
  .object({
    /** Programposten hos arrangøren. */
    performanceId: z.uuid(),
    /** BYRÅETS artistrad — lenken går til den. */
    bookingArtistId: z.uuid(),
    artistName: z.string(),
    organizerName: z.string(),
    /** Festivalen eller konsertens sted, og scenen: «God Nok-Festivalen · Hovedscenen». */
    where: z.string(),
    /** Dagen i arrangørens sone — det lista grupperer og fletter på. */
    startsOn: dateOnly,
    startsAt: instant.nullable(),
    status: performanceStatusSchema,
    enquiryId: z.uuid().nullable(),
    sharedAdvanceId: z.uuid().nullable(),
    feeMinor: z.number().int().nullable(),
    currency: z.string().length(3).nullable(),
    /** Engasjementet (0927, grep F1): samme status som Forespørsler leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
    /** By og kapasitet, lest fra arrangøren og budet gjennom porten (0930, QA13 114). */
    city: z.string().nullable().optional(),
    capacity: z.number().int().nullable().optional(),
  })
  .meta({ id: 'SoldGig' });
export type SoldGig = z.infer<typeof soldGigSchema>;

export const soldGigListResponseSchema = page(soldGigSchema, 'SoldGigListResponse');
export type SoldGigListResponse = z.infer<typeof soldGigListResponseSchema>;

/** Oppsummeringslinja over lista, og filtrenes valg. Regnet over hele huset, ikke én side. */
export const concertListStatsSchema = z
  .object({
    upcoming: z.number().int(),
    confirmed: z.number().int(),
    onHold: z.number().int(),
    holdsExpiringThisWeek: z.number().int(),
    advanceNotStarted: z.number().int(),
    /**
     * QA16 funn 23: de kommende solgte jobbene (byrået), talt med samme regel som lista. Null når
     * huset ikke selger. Sammendraget over «Alle» er egne + solgte, over «Solgte» bare disse.
     */
    sold: z
      .object({ upcoming: z.number().int(), confirmed: z.number().int(), onHold: z.number().int() })
      .nullable()
      .default(null),
    tags: z.array(z.string()),
    responsibles: z.array(personRefSchema),
  })
  .meta({ id: 'ConcertListStats' });
export type ConcertListStats = z.infer<typeof concertListStatsSchema>;

/**
 * `concertsModule`: plattformen har slått på «Konserter» for huset (0456, `organization_modules`) —
 * menyen viser modulen også før huset har laget sin første konsert.
 *
 * `hasSoldGigs` (F24, Atle 04.10): byrået har solgt en jobb til en arrangør. Da står «Konserter» i
 * menyen også uten modulen, med de solgte — før ble de bare nådd ved å skrive adressen.
 */
export const concertPresenceSchema = z
  .object({ hasConcerts: z.boolean(), concertsModule: z.boolean(), hasSoldGigs: z.boolean() })
  .meta({ id: 'ConcertPresence' });
export type ConcertPresence = z.infer<typeof concertPresenceSchema>;

/* ------------------------------------------------------------------------------ endre */

export const updateConcertRequestSchema = z
  .object({
    title: optionalText,
    startsOn: dateOnly.optional(),
    venueName: optionalText,
    roomName: optionalText,
    city: optionalText,
    address: z.string().trim().max(300).nullish(),
    doorsAt: concertClockTimeSchema.nullish(),
    curfewAt: concertClockTimeSchema.nullish(),
    getInAt: concertClockTimeSchema.nullish(),
    layoutName: optionalText,
    capacity: z.number().int().min(0).max(1_000_000).nullish(),
    expectedAudience: z.number().int().min(0).max(1_000_000).nullish(),
    status: z.enum(MANUAL_CONCERT_STATUSES).optional(),
    holdRank: z.number().int().min(1).max(3).nullish(),
    holdUntil: dateOnly.nullish(),
    /** «Merk som annonsert» og «I salg» setter datoen; status følger med. */
    announcedOn: dateOnly.nullish(),
    onSaleOn: dateOnly.nullish(),
    tag: optionalText,
    responsiblePersonId: z.uuid().nullish(),
    notes: z.string().trim().max(4000).nullish(),
    /** «Se endring» på salens spesifikasjon: arrangøren har sett siste versjon. */
    venueSpecSeen: z.boolean().optional(),
  })
  .meta({ id: 'UpdateConcertRequest' });
export type UpdateConcertRequest = z.infer<typeof updateConcertRequestSchema>;

/* ------------------------------------------------------------------------------ artistene */

/**
 * AVTALEN GJORT UTENFOR TVITVI (B1, DEL0 #22): engasjementet settes for hånd (253.72), og vilkårene
 * legges i et godtatt avtaleark på engasjementet. Ingen egen tabell. Brukt av arrangementet («Hvem
 * opptrer?») og av konserten («Ny konsert» og «Legg til artist», QA16 funn 31 og 302.7).
 */
export const outsideDealSchema = z
  .object({
    dealType: z.enum(CONCERT_DEAL_TYPES),
    feeMinor: z.number().int().min(0).max(100_000_000_00).nullable(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .default('NOK'),
    /** Kontrakten er signert (Signert), ellers Muntlig bekreftet. */
    signed: z.boolean(),
    /** Den signerte kontrakten, lastet opp som vedlegg. */
    contractAttachmentId: z.uuid().nullish(),
  })
  .meta({ id: 'OutsideDeal' });
export type OutsideDeal = z.infer<typeof outsideDealSchema>;

export const concertArtistInputSchema = z
  .object({
    bookingArtistId: z.uuid().nullish(),
    bookingArtistFormatId: z.uuid().nullish(),
    /** Fritekst, eller navnet fra registeret når id-en er gitt. */
    name: z.string().trim().min(1).max(160).nullish(),
    formatName: optionalText,
    /**
     * «Avtalen er gjort utenfor Tvitvi» (QA16 funn 31): bare for en artist fra Booking. Utelatt er
     * ingen avtale ennå — budet sendes fra konserten, eller artisten er organisasjonens egen.
     */
    outsideDeal: outsideDealSchema.nullish(),
  })
  .refine((value) => value.outsideDeal == null || value.bookingArtistId != null, {
    error: 'outside_deal_needs_artist',
    path: ['outsideDeal'],
  })
  .refine((value) => value.bookingArtistId != null || (value.name ?? '') !== '', {
    error: 'artist_required',
    path: ['name'],
  });
export type ConcertArtistInput = z.infer<typeof concertArtistInputSchema>;

export const addConcertArtistRequestSchema = z
  .object({ artist: concertArtistInputSchema, billing: concertBillingSchema })
  .meta({ id: 'AddConcertArtistRequest' });
export type AddConcertArtistRequest = z.infer<typeof addConcertArtistRequestSchema>;

export const updateConcertArtistRequestSchema = z
  .object({ billing: concertBillingSchema.optional(), formatName: optionalText })
  .meta({ id: 'UpdateConcertArtistRequest' });
export type UpdateConcertArtistRequest = z.infer<typeof updateConcertArtistRequestSchema>;

/* ------------------------------------------------------------------------------ kjøreplanen */

export const concertScheduleItemSchema = z
  .object({
    id: z.uuid(),
    concertId: z.uuid(),
    title: z.string(),
    startsAt: instant,
    endsAt: instant.nullable(),
    note: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'ConcertScheduleItem' });
export type ConcertScheduleItem = z.infer<typeof concertScheduleItemSchema>;

export const concertScheduleListSchema = z
  .object({ items: z.array(concertScheduleItemSchema) })
  .meta({ id: 'ConcertScheduleList' });
export type ConcertScheduleList = z.infer<typeof concertScheduleListSchema>;

/** Klokkeslettene er konsertens sone; `dayOffset` 1 er natta etter (load-out etter midnatt). */
export const saveConcertScheduleItemRequestSchema = z
  .object({
    title: shortText,
    startsAt: concertClockTimeSchema,
    endsAt: concertClockTimeSchema.nullish(),
    dayOffset: z.number().int().min(-1).max(1).default(0),
    note: z.string().trim().max(500).nullish(),
  })
  .meta({ id: 'SaveConcertScheduleItemRequest' });
export type SaveConcertScheduleItemRequest = z.infer<typeof saveConcertScheduleItemRequestSchema>;

/* ------------------------------------------------------------------------------ malen */

/** Hvordan en budsjettlinje fra malen er regnet. Beløpet er i øre; en prosent er i hundredeler. */
export const CONCERT_TEMPLATE_BUDGET_BASES = [
  'fixed',
  'percent_of_gross',
  'per_ticket',
  'per_person',
  'per_room',
] as const;
export const concertTemplateBudgetBasisSchema = z.enum(CONCERT_TEMPLATE_BUDGET_BASES);
export type ConcertTemplateBudgetBasis = z.infer<typeof concertTemplateBudgetBasisSchema>;

export const CONCERT_TEMPLATE_DEAL_TYPES = ['guarantee', 'versus', 'door_split', 'rent'] as const;
export const concertTemplateDealTypeSchema = z.enum(CONCERT_TEMPLATE_DEAL_TYPES);
export type ConcertTemplateDealType = z.infer<typeof concertTemplateDealTypeSchema>;

/** Minutter relativt til dørene: −420 er sju timer før. Et døgn hver vei er nok for en konsert. */
const offsetMinutes = z.number().int().min(-1440).max(1440);
const templateTitle = z.string().trim().min(1).max(120);

export const MAX_TEMPLATE_ROWS = 60;

/**
 * ÉN INNHOLDSFORM FOR MALENE (QA15-S B6, Del 0 #10). Salens maler (`venue_room_templates`) bruker den samme
 * formen som konsertmalene, utvidet med det salen trenger: en id per linje (vakta, reservasjonen og oppgaven
 * husker linja de kom fra), posten på en vaktlinje og ressursen med antall på en ressurslinje. Alt er
 * valgfritt, så en konsertmal som er lagret før, leses som før.
 */
const templateLineId = z.uuid().optional();

export const concertTemplateContentSchema = z
  .object({
    doorsAt: concertClockTimeSchema.nullable().default(null),
    curfewAt: concertClockTimeSchema.nullable().default(null),
    /** Get-in som klokkeslett mot `doorsAt` (B6). Følger dørene når malen brukes. */
    getInAt: concertClockTimeSchema.nullable().optional(),
    schedule: z
      .array(
        z.object({
          id: templateLineId,
          title: templateTitle,
          offsetMinutes,
          durationMinutes: z.number().int().min(0).max(1440).nullable().default(null),
        }),
      )
      .max(MAX_TEMPLATE_ROWS)
      .default([]),
    resources: z
      .array(
        z.object({
          id: templateLineId,
          resourceTypeId: z.uuid(),
          /** Typens navn da malen ble lagret, så malen leses selv om typen er slettet. */
          label: templateTitle,
          startOffsetMinutes: offsetMinutes,
          endOffsetMinutes: offsetMinutes,
          note: z.string().trim().max(200).nullable().default(null),
          /** B6: den bestemte ressursen (salens SM58), ikke bare typen. */
          resourceId: z.uuid().nullable().optional(),
          /** B6: hvor mange. Null er én, eller «vi teller ikke». */
          quantity: z.number().int().min(1).max(100_000).nullable().optional(),
          /** B6: reservasjonen dekker hele kvelden, fra get-in til curfew, og tidene over leses ikke. */
          spansEvening: z.boolean().optional(),
        }),
      )
      .max(MAX_TEMPLATE_ROWS)
      .default([]),
    posts: z
      .array(
        z.object({
          id: templateLineId,
          title: templateTitle,
          needed: z.number().int().min(1).max(50).default(1),
          startOffsetMinutes: offsetMinutes,
          endOffsetMinutes: offsetMinutes,
          /** B6: posten vakta står på. Påkrevd i salens maler (Del 0 #13); fri tittel lager posten. */
          staffingRoleId: z.uuid().nullable().optional(),
          /** B6: en ressurs på vakta, som en person (FUNN25 §c 6). */
          resourceId: z.uuid().nullable().optional(),
        }),
      )
      .max(MAX_TEMPLATE_ROWS)
      .default([]),
    tasks: z
      .array(
        z.object({
          id: templateLineId,
          title: templateTitle,
          /** Dager før konserten. Negativt er etter (oppgjør, takk-epost). */
          daysBefore: z.number().int().min(-365).max(365),
          description: z.string().trim().max(1000).nullable().default(null),
        }),
      )
      .max(MAX_TEMPLATE_ROWS)
      .default([]),
    budgetLines: z
      .array(
        z.object({
          kind: z.enum(['income', 'cost']),
          category: z.string().trim().max(80).nullable().default(null),
          description: templateTitle,
          basis: concertTemplateBudgetBasisSchema.default('fixed'),
          /** Øre, eller hundredeler av en prosent når `basis` er `percent_of_gross` (600 = 6 %). */
          amountMinor: z.number().int().min(0).max(MAX_COST_AMOUNT_MINOR),
        }),
      )
      .max(MAX_TEMPLATE_ROWS)
      .default([]),
    deal: z
      .object({
        dealType: concertTemplateDealTypeSchema,
        guaranteeMinor: z.number().int().min(0).max(MAX_COST_AMOUNT_MINOR).nullable().default(null),
        artistPercent: z.number().min(0).max(100).nullable().default(null),
        afterCosts: z.boolean().default(true),
      })
      .nullable()
      .default(null),
    advanceSections: z
      .array(
        z.object({
          name: templateTitle,
          /** Standardteksten. Hver linje blir en rad i seksjonen. */
          text: z.string().trim().max(4000).default(''),
        }),
      )
      .max(12)
      .default([]),
  })
  .meta({ id: 'ConcertTemplateContent' });
export type ConcertTemplateContent = z.infer<typeof concertTemplateContentSchema>;
export type ConcertTemplateContentInput = z.input<typeof concertTemplateContentSchema>;

export const concertTemplateDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    content: concertTemplateContentSchema,
    usedCount: z.number().int(),
    sortOrder: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'ConcertTemplate' });
export type ConcertTemplateDto = z.infer<typeof concertTemplateDtoSchema>;

export const concertTemplateListSchema = z
  .object({ items: z.array(concertTemplateDtoSchema) })
  .meta({ id: 'ConcertTemplateList' });
export type ConcertTemplateList = z.infer<typeof concertTemplateListSchema>;

export const createConcertTemplateRequestSchema = z
  .object({
    name: templateTitle,
    description: z.string().trim().max(500).nullish(),
    content: concertTemplateContentSchema.default(() => concertTemplateContentSchema.parse({})),
  })
  .meta({ id: 'CreateConcertTemplateRequest' });
export type CreateConcertTemplateRequest = z.infer<typeof createConcertTemplateRequestSchema>;

export const updateConcertTemplateRequestSchema = z
  .object({
    name: templateTitle.optional(),
    description: z.string().trim().max(500).nullish(),
    content: concertTemplateContentSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'UpdateConcertTemplateRequest' });
export type UpdateConcertTemplateRequest = z.infer<typeof updateConcertTemplateRequestSchema>;

export const saveConcertAsTemplateRequestSchema = z
  .object({ name: templateTitle, description: z.string().trim().max(500).nullish() })
  .meta({ id: 'SaveConcertAsTemplateRequest' });
export type SaveConcertAsTemplateRequest = z.infer<typeof saveConcertAsTemplateRequestSchema>;

/* ------------------------------------------------------------------------------ ny konsert */

export const CONCERT_SOURCE_KINDS = ['template', 'copy', 'empty'] as const;

export const concertSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('template'), templateId: z.uuid() }),
  z.object({ kind: z.literal('copy'), concertId: z.uuid() }),
  z.object({ kind: z.literal('empty') }),
]);
export type ConcertSource = z.infer<typeof concertSourceSchema>;

/** Hva hver konsert får. Hver del kan skrus av i steg 3 («Hver konsert får»). */
export const CONCERT_INCLUDE_PARTS = [
  'schedule',
  'resources',
  'posts',
  'tasks',
  'budget',
  'deal',
  'advance',
  'dateRequests',
] as const;
export type ConcertIncludePart = (typeof CONCERT_INCLUDE_PARTS)[number];

export const MAX_CONCERT_DATES = 40;

export const concertDateInputSchema = z.object({
  startsOn: dateOnly,
  city: optionalText,
  venueName: optionalText,
  roomName: optionalText,
  /** En sal i Tvitvi: datoforespørselen sendes med én gang (C). */
  venueRoomId: z.uuid().nullish(),
  venueOrganizationId: z.uuid().nullish(),
  /** Et sted i stedsregisteret. */
  venueDirectoryRoomId: z.uuid().nullish(),
  doorsAt: concertClockTimeSchema.nullish(),
  curfewAt: concertClockTimeSchema.nullish(),
  layoutName: optionalText,
  capacity: z.number().int().min(0).max(1_000_000).nullish(),
});
export type ConcertDateInput = z.infer<typeof concertDateInputSchema>;

export const createConcertBatchRequestSchema = z
  .object({
    source: concertSourceSchema,
    headliner: concertArtistInputSchema.nullable(),
    support: z.array(concertArtistInputSchema).max(6).default([]),
    dates: z.array(concertDateInputSchema).min(1).max(MAX_CONCERT_DATES),
    tag: optionalText,
    responsiblePersonId: z.uuid().nullish(),
    /** «Vi arrangerer selv» (byrå) er `own`, som for en arrangør. */
    productionMode: z.enum(['own', 'sold']).default('own'),
    include: z
      .object({
        schedule: z.boolean().default(true),
        resources: z.boolean().default(true),
        posts: z.boolean().default(true),
        tasks: z.boolean().default(true),
        budget: z.boolean().default(true),
        deal: z.boolean().default(true),
        advance: z.boolean().default(true),
        dateRequests: z.boolean().default(true),
      })
      .default({
        schedule: true,
        resources: true,
        posts: true,
        tasks: true,
        budget: true,
        deal: true,
        advance: true,
        dateRequests: true,
      }),
  })
  .meta({ id: 'CreateConcertBatchRequest' });
export type CreateConcertBatchRequest = z.infer<typeof createConcertBatchRequestSchema>;
export type CreateConcertBatchInput = z.input<typeof createConcertBatchRequestSchema>;

export const createConcertBatchResponseSchema = z
  .object({
    concerts: z.array(concertDtoSchema),
    dateRequestsSent: z.number().int(),
    /** Ressurstyper uten en ressurs å reservere, per navn. Konserten lages likevel. */
    skippedResources: z.array(z.string()),
  })
  .meta({ id: 'CreateConcertBatchResponse' });
export type CreateConcertBatchResponse = z.infer<typeof createConcertBatchResponseSchema>;

/* ------------------------------------------------------------------------------ oversikten */

export const CONCERT_MISSING_KEYS = [
  'rider',
  /* `travelling_party` siden 26.10 (§8.2); serveren sender den. Den gamle godtas en stund. */
  'travelling_party',
  'travellingParty',
  'hotel',
  'localTransport',
  'houseCrew',
  'venueSpecChanged',
  'supportRider',
] as const;
export const concertMissingKeySchema = z.enum(CONCERT_MISSING_KEYS);
export type ConcertMissingKey = z.infer<typeof concertMissingKeySchema>;

export const concertMissingItemSchema = z.object({
  key: concertMissingKeySchema,
  /** `ok` er huket av, `missing` mangler, `info` er et tall uten dom. */
  state: z.enum(['ok', 'missing', 'info']),
  performanceId: z.uuid().nullable(),
  artistName: z.string().nullable(),
  count: z.number().int().nullable(),
  total: z.number().int().nullable(),
  at: z.iso.datetime().nullable(),
});
export type ConcertMissingItem = z.infer<typeof concertMissingItemSchema>;

export const CONCERT_PARTY_KINDS = ['us', 'venue', 'agency', 'artists'] as const;

export const concertPartySchema = z.object({
  kind: z.enum(CONCERT_PARTY_KINDS),
  name: z.string(),
  /** Hvilken artist et byrå er med for. */
  artistName: z.string().nullable(),
});
export type ConcertParty = z.infer<typeof concertPartySchema>;

export const concertCrewSummarySchema = z.object({
  shifts: z.number().int(),
  needed: z.number().int(),
  assigned: z.number().int(),
});
export type ConcertCrewSummary = z.infer<typeof concertCrewSummarySchema>;

export const concertOverviewSchema = z
  .object({
    concertId: z.uuid(),
    parties: z.array(concertPartySchema),
    missing: z.array(concertMissingItemSchema),
    /** Null når kvelden ikke finnes hos huset ennå. */
    houseCrew: concertCrewSummarySchema.nullable(),
  })
  .meta({ id: 'ConcertOverview' });
export type ConcertOverview = z.infer<typeof concertOverviewSchema>;

/* ------------------------------------------------------------------------------ kalenderen */

export const CONCERT_TIMELINE_KINDS = ['schedule', 'task', 'allocation', 'shift'] as const;

export const concertTimelineEntrySchema = z.object({
  kind: z.enum(CONCERT_TIMELINE_KINDS),
  id: z.uuid(),
  title: z.string(),
  startsAt: instant,
  endsAt: instant.nullable(),
  /** Oppgaven: ferdig eller ikke. Reservasjonen: ressursens navn. */
  detail: z.string().nullable(),
  done: z.boolean(),
});
export type ConcertTimelineEntry = z.infer<typeof concertTimelineEntrySchema>;

export const concertTimelineSchema = z
  .object({ entries: z.array(concertTimelineEntrySchema) })
  .meta({ id: 'ConcertTimeline' });
export type ConcertTimeline = z.infer<typeof concertTimelineSchema>;

/* ------------------------------------------------------------------------------ personer */

export const concertShiftSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  startsAt: instant,
  endsAt: instant,
  needed: z.number().int(),
  people: z.array(personRefSchema),
});
export type ConcertShift = z.infer<typeof concertShiftSchema>;

export const concertShiftListSchema = z
  .object({ items: z.array(concertShiftSchema) })
  .meta({ id: 'ConcertShiftList' });
export type ConcertShiftList = z.infer<typeof concertShiftListSchema>;

export const createConcertShiftRequestSchema = z
  .object({
    title: shortText,
    startsAt: concertClockTimeSchema,
    endsAt: concertClockTimeSchema,
    /** 1 når slutten er etter midnatt. */
    endDayOffset: z.number().int().min(0).max(1).default(0),
    needed: z.number().int().min(1).max(50).default(1),
    personIds: z.array(z.uuid()).max(50).default([]),
  })
  .meta({ id: 'CreateConcertShiftRequest' });
export type CreateConcertShiftRequest = z.infer<typeof createConcertShiftRequestSchema>;

/* ------------------------------------------------------------------------------ ressurser */

export const concertAllocationSchema = z.object({
  id: z.uuid(),
  resourceId: z.uuid(),
  resourceName: z.string(),
  resourceTypeName: z.string(),
  title: z.string(),
  startsAt: instant,
  endsAt: instant,
  note: z.string().nullable(),
});
export type ConcertAllocation = z.infer<typeof concertAllocationSchema>;

export const concertResourcesSchema = z
  .object({
    items: z.array(concertAllocationSchema),
    /** Husets egne ressurser, til «Reserver». */
    available: z.array(
      z.object({ id: z.uuid(), name: z.string(), typeId: z.uuid(), typeName: z.string() }),
    ),
    /** Husets ressurstyper, til «Ny ressurs». */
    types: z.array(z.object({ id: z.uuid(), name: z.string() })),
  })
  .meta({ id: 'ConcertResources' });
export type ConcertResources = z.infer<typeof concertResourcesSchema>;

export const createConcertAllocationRequestSchema = z
  .object({
    resourceId: z.uuid(),
    title: shortText.optional(),
    startsAt: concertClockTimeSchema,
    endsAt: concertClockTimeSchema,
    endDayOffset: z.number().int().min(0).max(1).default(0),
    note: z.string().trim().max(200).nullish(),
  })
  .meta({ id: 'CreateConcertAllocationRequest' });
export type CreateConcertAllocationRequest = z.infer<typeof createConcertAllocationRequestSchema>;

export const createConcertResourceRequestSchema = z
  .object({ resourceTypeId: z.uuid(), name: shortText })
  .meta({ id: 'CreateConcertResourceRequest' });
export type CreateConcertResourceRequest = z.infer<typeof createConcertResourceRequestSchema>;
